import React, { useRef } from 'react';
import { useLongPress } from '@/hooks/useLongPress';
import { ContactLogic } from '@/modules/contact';
import { INTENT_SYMBOL } from '@/modules/share/logic';
import { OutboxLogic } from './logic';
import type { OutboxItem, OutboxRowStatus } from './types';
import styles from './OutboxRow.module.css';

/**
 * The mark beside each row, as SVG path data drawn with one heavy stroke:
 * none until the row is in play. Drawn here rather than taken from ionicons,
 * whose arrow and question mark fix their own stroke width.
 */
const STATUS_MARK: Record<OutboxRowStatus, string | undefined> = {
  ready: undefined,
  upNext: 'M4 12h15M13 5.5l6.5 6.5-6.5 6.5',
  toConfirm:
    'M8 8.5a4 4 0 1 1 5.6 3.7c-1 .45-1.6 1.4-1.6 2.5v.8M12 19.5v.01',
  sent: 'M4.5 12.5l5 5 10-11',
};

/** What a row shows: one message, who it reaches, and where it stands. */
export const OutboxRowContent: React.FC<{
  item: OutboxItem;
  status: OutboxRowStatus;
}> = ({ item, status }) => {
  const { recipients } = item;
  const isGroup = recipients.length > 1;
  const mark = STATUS_MARK[status];
  return (
    <span className={`${styles.content} ${styles[status]}`}>
      <span className={styles.indicator} aria-hidden="true">
        {mark && (
          <svg viewBox="0 0 24 24">
            <path d={mark} />
          </svg>
        )}
      </span>
      <span className={styles.avatar} aria-hidden="true">
        {isGroup ? recipients.length : ContactLogic.getInitials(recipients[0])}
      </span>
      <span className={styles.who}>
        <span className={styles.name}>
          {OutboxLogic.rowName(recipients)} {INTENT_SYMBOL[item.leg.intent]}
        </span>
        {isGroup && (
          <span className={styles.detail}>
            {OutboxLogic.groupLabel(recipients)}
          </span>
        )}
      </span>
      <span className={styles.statusLabel}>
        {OutboxLogic.statusLabel(status)}
      </span>
    </span>
  );
};

/**
 * One line of the outbox. A tap previews the message; a long-press lifts the
 * row and offers what can be done with it, so the list itself never needs a
 * button per row.
 */
const OutboxRow: React.FC<{
  item: OutboxItem;
  status: OutboxRowStatus;
  onTap: () => void;
  onLongPress: (anchor: DOMRect) => void;
}> = ({ item, status, onTap, onLongPress }) => {
  const ref = useRef<HTMLDivElement>(null);
  const handlers = useLongPress(() => {
    if (ref.current) onLongPress(ref.current.getBoundingClientRect());
  }, onTap);

  return (
    <li className={styles.row}>
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        aria-label={`${OutboxLogic.rowName(item.recipients)}, ${
          OutboxLogic.statusLabel(status) || 'not sent'
        }. Long-press for actions.`}
        className={styles.press}
        data-testid={`outbox-row-${item.id}`}
        data-status={status}
        {...handlers}
      >
        <OutboxRowContent item={item} status={status} />
      </div>
    </li>
  );
};

export default OutboxRow;
